import { useState } from 'react';
import { App as AntApp, Button, Card, Form, Input, Modal, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useBankAccounts, useCreateBankAccount, type BankAccount } from '../api/payments';
import { asApiError } from '@/shared/lib/api';

export function BankAccountsPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { data, isLoading } = useBankAccounts();
  const create = useCreateBankAccount();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  async function onOk() {
    try {
      const v = await form.validateFields();
      await create.mutateAsync(v);
      void message.success(t('payments.bankAccountCreated'));
      setOpen(false);
      form.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('payments.bankAccountsTitle')}</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          {t('payments.bankAccountNew')}
        </Button>
      </div>
      <Card bodyStyle={{ padding: 0 }}>
        <Table<BankAccount>
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          pagination={false}
          columns={[
            { title: t('payments.bankAccountCode'), dataIndex: 'code', width: 120 },
            { title: t('payments.bankAccountBank'), dataIndex: 'bankName' },
            { title: t('payments.bankAccountNumber'), dataIndex: 'accountNumber' },
            { title: t('payments.bankAccountCurrency'), dataIndex: 'currency', width: 80 },
            { title: t('payments.bankAccountActive'), dataIndex: 'active', width: 100, render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '●' : '—'}</Tag> },
          ]}
        />
      </Card>

      <Modal
        title={t('payments.bankAccountNewTitle')}
        open={open}
        onOk={onOk}
        onCancel={() => setOpen(false)}
        confirmLoading={create.isPending}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ currency: 'UZS' }}>
          <Form.Item name="code" label={t('payments.bankAccountCode')} rules={[{ required: true }]}>
            <Input placeholder="NBU-MAIN" />
          </Form.Item>
          <Form.Item name="bankName" label={t('payments.bankAccountBank')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="accountNumber" label={t('payments.bankAccountNumber')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="currency" label={t('payments.bankAccountCurrency')}>
            <Input maxLength={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
